import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import type { Attachment } from '@rundown/types';
import { presignUpload, registerAttachment, uploadToStore } from './api';
import { optimiseImage } from './optimiseImage';

/**
 * One upload queue, shared by the three places files can enter Rundown: the ask
 * box, the description editor and the message composer.
 *
 * The flow per file is optimise → presign → POST to the store → register. Each
 * file runs independently, so one failure among five pasted screenshots leaves
 * the other four attached rather than losing the batch.
 *
 * Uploads start immediately, before the request or message they will belong to
 * exists. That is what makes pasting feel instant, and it is why the server
 * accepts an attachment with no parent and links it afterwards — by the time
 * Send is pressed the bytes are already there and only the ids travel.
 */

export interface PendingUpload {
  readonly localId: string;
  readonly filename: string;
  readonly byteSize: number;
  readonly contentType: string;
  /** 0–1 while sending; 1 once the store has it. */
  readonly progress: number;
  readonly status: 'optimising' | 'uploading' | 'done' | 'error';
  readonly error?: string;
  /** Set once registered. This id is what gets linked to the request. */
  readonly attachment?: Attachment;
  /** Local preview, shown before the signed URL exists. Revoked on clear. */
  readonly previewUrl?: string;
  /** Bytes saved by the browser-side re-encode, when it did anything. */
  readonly savedBytes?: number;
}

export interface UploaderTarget {
  readonly requestId?: string | null;
  readonly messageId?: string | null;
}

let counter = 0;
const nextLocalId = () => `upload-${Date.now().toString(36)}-${(counter += 1)}`;

export function useUploader(workspaceId: string, target: UploaderTarget = {}) {
  const [uploads, setUploads] = useState<readonly PendingUpload[]>([]);
  const previewUrls = useRef<string[]>([]);

  const patch = useCallback((localId: string, changes: Partial<PendingUpload>) => {
    setUploads((current) =>
      current.map((upload) => (upload.localId === localId ? { ...upload, ...changes } : upload)),
    );
  }, []);

  /**
   * Upload files and RETURN the attachments that landed.
   *
   * The return value is what lets a caller put a pasted screenshot inline in
   * the document: an image node stores an attachment id, so the id has to come
   * back. Callers that only want the file attached (a PDF, the paperclip
   * button) ignore it, which is why this stayed `void` for as long as the
   * strip was the only destination.
   *
   * Only the ones that succeeded. A failed upload is already visible in
   * `uploads` with its error, and returning a half-made attachment would put a
   * node in the document pointing at nothing.
   */
  const addFiles = useCallback(
    async (files: readonly File[]): Promise<Attachment[]> => {
      if (files.length === 0) return [];

      const started = files.map((file) => {
        const localId = nextLocalId();
        // Object URL, not a data URL: a data URL for a 10 MB screenshot is a
        // 13 MB string held in React state until the upload finishes.
        const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
        if (previewUrl) previewUrls.current.push(previewUrl);

        return {
          upload: {
            localId,
            filename: file.name || 'pasted-image.png',
            byteSize: file.size,
            contentType: file.type || 'application/octet-stream',
            progress: 0,
            status: 'optimising' as const,
            previewUrl,
          },
          file,
        };
      });

      setUploads((current) => [...current, ...started.map((entry) => entry.upload)]);

      const landed: Attachment[] = [];

      await Promise.all(
        started.map(async ({ upload, file }) => {
          try {
            const { file: toSend, optimised, originalBytes } = await optimiseImage(file);

            patch(upload.localId, {
              status: 'uploading',
              filename: toSend.name,
              byteSize: toSend.size,
              contentType: toSend.type,
              savedBytes: optimised ? originalBytes - toSend.size : undefined,
            });

            const presigned = await presignUpload(workspaceId, {
              filename: toSend.name,
              contentType: toSend.type || 'application/octet-stream',
              byteSize: toSend.size,
            });

            await uploadToStore(presigned, toSend, (fraction) =>
              patch(upload.localId, { progress: fraction }),
            );

            const attachment = await registerAttachment(workspaceId, {
              objectKey: presigned.objectKey,
              filename: toSend.name,
              contentType: toSend.type || 'application/octet-stream',
              requestId: target.requestId ?? null,
              messageId: target.messageId ?? null,
            });

            patch(upload.localId, { status: 'done', progress: 1, attachment });
            landed.push(attachment);
          } catch (error) {
            patch(upload.localId, {
              status: 'error',
              error: error instanceof Error ? error.message : 'That file could not be attached.',
            });
          }
        }),
      );

      return landed;
    },
    [workspaceId, target.requestId, target.messageId, patch],
  );

  /**
   * Paste.
   *
   * `items` rather than `files`, because a screenshot pasted from the clipboard
   * arrives as an item with no filename at all — reading `files` alone misses
   * the single most common case this feature exists for.
   *
   * Returns true when something was taken, so the caller can suppress the
   * default paste only then: pasting text must still insert text.
   */
  const handlePaste = useCallback(
    (event: ClipboardEvent): boolean => {
      const files: File[] = [];
      for (const item of event.clipboardData.items) {
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (file !== null) files.push(file);
      }
      if (files.length === 0) return false;
      event.preventDefault();
      void addFiles(files);
      return true;
    },
    [addFiles],
  );

  const handleDrop = useCallback(
    (event: DragEvent): boolean => {
      const files = [...event.dataTransfer.files];
      if (files.length === 0) return false;
      event.preventDefault();
      void addFiles(files);
      return true;
    },
    [addFiles],
  );

  const remove = useCallback((localId: string) => {
    setUploads((current) => current.filter((upload) => upload.localId !== localId));
  }, []);

  /** Call after the parent request or message has been created. */
  const clear = useCallback(() => {
    for (const url of previewUrls.current) URL.revokeObjectURL(url);
    previewUrls.current = [];
    setUploads([]);
  }, []);

  /** Ids to hand to the create call. Only the ones that actually landed. */
  const attachmentIds = uploads
    .filter((upload) => upload.attachment !== undefined)
    .map((upload) => upload.attachment!.id);

  return {
    uploads,
    attachmentIds,
    /** True while anything is still in flight — used to hold the Send button. */
    isUploading: uploads.some((upload) => upload.status === 'optimising' || upload.status === 'uploading'),
    addFiles,
    handlePaste,
    handleDrop,
    remove,
    clear,
  };
}
